import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import config from './config';
import { useBpFlags } from './Responsive';

import i18n from "./i18n";
const tr = (key, vars) => i18n.t(key, vars);

const apiGet = async (path) => {
  const r = await fetch(tr('{{API_BASE_URL}}{{path}}', {
    API_BASE_URL: config.API_BASE_URL,
    path
  }), { credentials: 'include' });
  if (!r.ok) throw new Error(tr('API failed: {{path}}', {
    path
  }));
  return r.json();
};
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randoms = { client: null, table: null, meat: null, cooktype: null };

async function preloadRandoms() {
  // clients
  const clients = await apiGet('/clients?limit=200');
  randoms.client = pick(clients.filter(c => c && (c.firstname || c.lastname) && c.phone));

  // decide search mode: first 4 letters of name OR full phone (random)
  const nameSource =
    (randoms.client?.lastname && randoms.client.lastname.length >= 4)
      ? randoms.client.lastname
      : (randoms.client?.firstname || randoms.client?.lastname || '');
  const nameTerm = (nameSource || '').slice(0, 4);

  let usePhone = Math.random() < 0.5 && !!randoms.client?.phone;
  // if the name is too short, fall back to phone
  if (!usePhone && nameTerm.length < 2) usePhone = true;

  randoms.clientSearch = usePhone
    ? { mode: 'phone', term: randoms.client.phone?.slice(0, 4) }
    : { mode: 'name',  term: nameTerm };

  // meats & cooktypes
  const meats = await apiGet('/meats');
  const cooktypes = await apiGet('/cooktypes');
  randoms.meat = pick(meats);
  randoms.cooktype = pick(cooktypes);

  // tables (filter by current people if available)
  const people = parseInt(document.querySelector('input[name="people"]')?.value || '5', 10);
  const tables = await apiGet('/tables/view');
  const candidates = tables.filter(t => t.isAvailable_window && Number(t.capacity) >= people);
  randoms.table = pick(candidates.length ? candidates : tables);
}

function FloatingNote({ x, y, text, emphasize = false, timerMs = 0, instance = 0 }) {

  const ref = useRef(null);
  const [style, setStyle] = useState({
    left: -9999,
    top: -9999,
    transform: "translate(0,0)",
    arrowSide: "right",
  });

  useLayoutEffect(() => {
    if (!text) return;

    const margin = 12;   // viewport margin
    const offset = 23;   // gap from cursor
    const w = ref.current?.offsetWidth ?? 280;
    const h = ref.current?.offsetHeight ?? 80;

    let left = x + offset;
    let top  = y + offset;
    let tX = 0;
    let tY = 0;
    let arrowSide = "right"; // default arrow on the left side of the bubble

    // Flip horizontally if overflowing right edge
    if (left + w + margin > window.innerWidth) {
      left = x - offset;
      tX = -100; // anchor to the right of the bubble
      arrowSide = "left";
    }
    // Flip vertically if overflowing bottom edge
    if (top + h + margin > window.innerHeight) {
      top = y - offset;
      tY = -100; // anchor to bottom
    }

    setStyle({ left, top, transform: tr('translate({{tX}}%, {{tY}}%)', {
      tX,
      tY
    }), arrowSide });
  }, [x, y, text]);

  if (!text) return null;

  return (
    <div
      ref={ref}
      style={{
        position: "fixed",
        left: style.left,
        top: style.top,
        transform: style.transform,
        zIndex: 99998,
        pointerEvents: "none",
        background: emphasize ? "rgba(17,17,17,.9)" : "rgba(17,17,17,.75)",
        color: "#fff",
        padding: emphasize ? "15px 20px" : "10px 12px",
        border: (emphasize ? 3: 1)+"px solid darkOrange",
        borderRadius: emphasize ? "20px" : "0 20px 20px",
        maxWidth: 300,
        fontSize: 14,
        lineHeight: 1.4,
        boxShadow: "0 8px 24px rgba(0,0,0,.18)",
        textShadow: "1px 1px 1px black",
        transition: "left 160ms ease, top 160ms ease, transform 1000ms ease, border 500ms ease, border-radius 500ms ease, background 500ms ease, padding 500ms ease"
      }}
    >
      {text}
      {timerMs > 0 && (
        <div
          style={{
            position: "absolute",
            left: emphasize ? 15 : 10,
            right: emphasize ? 15 : 10,
            bottom: 2,             // hug the bottom
            height: 1,             // thin bar
            background: "rgba(255,255,255,.18)",
            borderRadius: 999,
            overflow: "hidden",
          }}
        >
          <div
            key={`${instance}-${timerMs}`} // remount each note to reset animation
            style={{
              height: "100%",
              width: "100%",
              background: "linear-gradient(90deg, orange, gold)",
              transformOrigin: "left center",
              transform: "scaleX(0)",                 // starts empty
              animation: tr('noteFill {{timerMs}}ms linear forwards', {
                timerMs
              }),
            }}
          />
          <style>
            {tr(
              '@keyframes noteFill { from { transform: scaleX(0); } to { transform: scaleX(1); } }'
            )}
          </style>
        </div>
      )}
    </div>
  );
}

/** Utilities **/
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const SPEED = 7;                  // 1.0 = normal, 0.8 = faster, 1.2 = slower // 7

const wait = (ms) => sleep(ms * SPEED);

const viewportCenter = () => ({
  x: Math.round(window.innerWidth / 2),
  y: Math.round(window.innerHeight / 2),
});

const waitForSelector = async (selector, { timeout = 8000 } = {}) => {
  const start = performance.now();
  while (performance.now() - start < timeout) {
    const el = document.querySelector(selector);
    if (el) return el;
    await wait(50);
  }
  throw new Error(tr('Timeout waiting for {{selector}}', {
    selector
  }));
};

const setNativeValue = (el, value) => {
  const proto =
    el.tagName === "TEXTAREA"
      ? window.HTMLTextAreaElement.prototype
      : window.HTMLInputElement.prototype;
  const valueSetter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
  valueSetter ? valueSetter.call(el, value) : (el.value = value);
  el.dispatchEvent(new Event("input", { bubbles: true })); // ensure React onChange runs
};

const typeText = async (selector, text, delay = 30) => {
  const el = await waitForSelector(selector);
  el.focus();
  setNativeValue(el, ""); // clear through native setter
  for (const ch of text) {
    setNativeValue(el, (el.value ?? "") + ch);
    await wait(delay);
  }
};

const clickEl = (el) => {
  const fire = (t) =>
    el.dispatchEvent(new MouseEvent(t, { bubbles: true, cancelable: true, view: window }));
  fire("pointerdown"); fire("mousedown"); fire("pointerup"); fire("mouseup"); fire("click");
};

/** Fake cursor **/
function Cursor({ x, y, clicking, visible = true, progress = 0, progressAnimMs = 600 }) {
  // Cursor box is 28×28; ring sits *inside* this box
  const BOX = 28;
  const STROKE = 2;
  const R = (BOX / 2) - (STROKE / 2);     // radius so stroke stays inside the box
  const C = 2 * Math.PI * R;              // circumference

  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        transform: tr('translate({{x}}px, {{y}}px)', {
          x,
          y
        }),
        width: BOX,
        height: BOX,
        borderRadius: "50%",
        background: "rgba(17,17,17,.85)",
        boxShadow: "0 0 0 3px rgba(17,17,17,.15)",
        zIndex: 99999,
        opacity: visible ? 1 : 0.75,
        transition: "transform 400ms cubic-bezier(.22,1,.36,1), opacity 500ms ease",
        pointerEvents: "none",
      }}
    >
      {/* Progress ring INSIDE the cursor; rotate -90° so it starts at 12 o’clock */}
      <svg
        width={BOX}
        height={BOX}
        viewBox={tr('0 0 {{BOX}} {{BOX}}', {
          BOX,
          BOX
        })}
        style={{ position: "absolute", left: 0, top: 0 }}
      >
        <g transform={tr('rotate(-90 {{var1}} {{var2}})', {
          var1: BOX / 2,
          var2: BOX / 2
        })}>
          <circle
            cx={BOX / 2}
            cy={BOX / 2}
            r={R}
            fill="none"
            stroke="rgba(255,255,255,.18)"
            strokeWidth={STROKE}
          />
          <circle
            cx={BOX / 2}
            cy={BOX / 2}
            r={R}
            fill="none"
            stroke="orange"
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={C}
            strokeDashoffset={Math.max(C * (1 - progress), 0)}
            style={{ transition: tr('stroke-dashoffset {{progressAnimMs}}ms linear', {
              progressAnimMs
            }) }}
          />
        </g>
      </svg>
      {clicking && (
        <span
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%,-50%)",
            width: 24,
            height: 24,
            borderRadius: "50%",
            border: "2px solid rgba(17,17,17,.25)",
            animation: "pulse 450ms ease-out",
          }}
        />
      )}
      <style>
        {tr(
          '@keyframes pulse{from{opacity:1;transform:translate(-50%,-50%) scale(.6)}to{opacity:0;transform:translate(-50%,-50%) scale(1.4)}}'
        )}
      </style>
    </div>
  );
}

/** DemoPlayer **/
export default function DemoPlayer({ onDone }) {

  const navigate = useNavigate();
  const location = useLocation();
  const [note, setNote] = useState("");
  const [cursor, setCursor] = useState(() => ({ ...viewportCenter(), clicking: false }));
  const [cursorVisible, setCursorVisible] = useState(true);
  const [emphasizeNote, setEmphasizeNote] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressAnimMs, setProgressAnimMs] = useState(600);
  const [noteTimerMs, setNoteTimerMs] = useState(0);
  const [noteInstance, setNoteInstance] = useState(0);
  const { isDesktop, isTablet, isMobile } = useBpFlags();
  const [deviceBlocked, setDeviceBlocked] = useState(false);

  // move cursor to a point (no scrolling)
  const moveToPoint = async (x, y) => {
    setCursor(c => ({ ...c, x, y }));
    await wait(220);
  };

  // click whatever is under the given cell’s center (robust to re-render)
  const clickCellCenter = async (cell) => {
    const r = cell.getBoundingClientRect();
    const x = r.left + r.width / 2;
    const y = r.top + r.height / 2;
    await moveToPoint(x, y);
    const target = document.elementFromPoint(x, y) || cell;
    await click(target);
  };

  const centerCursor = async () => {
    const { x, y } = viewportCenter();
    const X = x*4/5;
    const Y = y*4/5;
    setCursor((c) => ({ ...c, x: X, y: Y }));
    await wait(200);
  };

  const runningRef = useRef(false);

  // If the user interacts, cancel the demo.
  const cancelledRef = useRef(false);
  useEffect(() => {
    const onUser = (e) => {
      // Real user events are trusted; our synthetic clickEl dispatches are not.
      if (e.isTrusted) {
        cancelledRef.current = true;
      }
    };
    window.addEventListener('click', onUser, true);
    window.addEventListener('pointerdown', onUser, true);
    window.addEventListener('keydown', onUser, true);
    window.addEventListener('wheel', onUser, { capture: true, passive: true });
    return () => {
      window.removeEventListener('click', onUser, true);
      window.removeEventListener('pointerdown', onUser, true);
      window.removeEventListener('keydown', onUser, true);
      window.removeEventListener('wheel', onUser, { capture: true });
    };
  }, []);

  const moveToEl = async (el, opts = {}) => {
    if (opts.scroll !== false) {
      el.scrollIntoView({ block: "center", inline: "center", behavior: "auto" });
    }
  const r = el.getBoundingClientRect();
  setCursor(c => ({ ...c, x: r.left + r.width / 2, y: r.top + r.height / 2 }));
  await wait(320); // uses the SPEED-aware wait()
};

  // Smooth scroll to page top (keep as you had)
  const smoothScrollTop = async () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    await wait(650);
  };

  const click = async (el) => {
      setCursor(c => ({ ...c, clicking: true }));
      clickEl(el);
      await wait(120);
      setCursor(c => ({ ...c, clicking: false }));
  };

  // ---------- Visible-only helpers for Service ActionsList ----------

  // DemoPlayer.jsx (add near your other helpers)
  const isVisible = (el) => !!el && el.offsetParent !== null && el.getClientRects().length > 0;

  const getVisibleRightPanel = () =>
      Array.from(document.querySelectorAll('.service-container .right-panel')).find(isVisible) || null;

  const findDoneIconByTableAndType = (tableNumber, typeId, root = document) => {
    const suffix = tr('{{tableNumber}}_{{typeId}}', {
      tableNumber,
      typeId
    });
    const nodes = Array.from(root.querySelectorAll(tr('b[data-done-key$="{{suffix}}"]', {
      suffix
    })))
      .filter(isVisible);
    return nodes && nodes.length>0 ? nodes[0] : null;
  };

  const waitForCondition = async (fn, { timeout = 2000, interval = 80 } = {}) => {
    const t0 = performance.now();
    while (performance.now() - t0 < timeout) {
      if (fn()) return true;
      await wait(interval);
    }
    return false;
  };

  const clickDoneByTableAndType = async (tableNumber, typeId, { timeout = 2500 } = {}) => {
    const panel = getVisibleRightPanel();
    const root  = panel || document;
    const list  = root.querySelector('.service-events-container') || root;

    // Bring the right panel into view
    panel?.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
    await wait(250);

    const deadline = performance.now() + timeout;
    while (performance.now() < deadline) {
      // resolve the current ✓ *inside the right panel*
      const btn = findDoneIconByTableAndType(tableNumber, typeId, root);
      if (btn) {
        const td = btn.closest('td');
        const tr = btn.closest('tr');
        // keep both the list and row in view
        list?.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
        tr?.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
        td?.scrollIntoView?.({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
        await wait(120);

        await moveToEl(btn);   // positions the fake cursor
        await click(btn);      // dispatches real-looking click events

        // verify the ✓ became the 6s pending ring
        const ok = await waitForCondition(
          () => !!td?.querySelector('.progress-container'),
          { timeout: 1800, interval: 80 }
        );
        if (ok) return; // success
      }
          
      await wait(220);
    }
    throw new Error(tr(
      'Done icon not found/clicked for table {{tableNumber}} & type {{typeId}}',
      {
        tableNumber,
        typeId
      }
    ));
  };

  const getVisibleTablesContainer = () => {
    const candidates = [
      ...document.querySelectorAll('.rf-selector-right'),
      ...document.querySelectorAll('.rf-selector-bottom'),
    ];
    return candidates.find(isVisible) || null;
  };

  const findByText = (text, { root = document, selector = "*" } = {}) => {
    const els = Array.from(root.querySelectorAll(selector)).filter(isVisible);
    for (const el of els) {
      const t = (el.textContent || "").trim();
      if (!t) continue;
      if (text instanceof RegExp ? text.test(t) : t.includes(text)) return el;
    }
    return null;
  };

  const waitForText = async (text, { root = document, selector = "*", timeout = 8000 } = {}) => {
    const start = performance.now();
    while (performance.now() - start < timeout) {
      const el = findByText(text, { root, selector });
      if (el) return el;
      await wait(50);
    }
    throw new Error(tr('Timeout waiting for text: {{text}}', {
      text
    }));
  };

  // --- Robust DOM helpers for Service/Kitchen actions ---

  const queryAll = (root, sel) => Array.from((root || document).querySelectorAll(sel));

  const findServiceRoot = () =>
    document.querySelector('.service-events-container, .reservation-events-container, .service-container .right-panel') || document;

  const clickSittingButton = async () => {
    // WalkedInSitting renders in Service toolbar/left panel (see Service.js).  Try to find a button by text.
    const btn = findByText(/^\s*Sitting\s*$/i, { root: document, selector: 'button,*' });
    if (!btn) throw new Error("Sitting button not found");
    await moveToEl(btn);
    await click(btn);
  };

  const clickKitchenLastReady = async () => {
    // Kitchen table has a last column "Action" with a div[title="Ready"] and a ✓ (see Kitchen.js render).
    const tbody = document.querySelector('.kitchenTable tbody') || document.querySelector('table tbody');
    if (!tbody) throw new Error("Kitchen table not found");
    const lastRow = tbody.querySelector('tr:last-child');
    if (!lastRow) throw new Error("No rows found in Kitchen");
    const readyBtn =
      lastRow.querySelector('div[title="Ready"]') ||
      [...lastRow.querySelectorAll('button,[role="button"],[title],span,b,i')].find(n =>
        /ready|✓/i.test((n.getAttribute?.('title') || n.textContent || ''))
      );
    if (!readyBtn) throw new Error('No "Ready" control found in last Kitchen row');
    await moveToEl(readyBtn);
    await click(readyBtn);
  };

  const setValue = (el, value) => {
    el.focus();
    el.value = value;
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const stepPreloadRandoms = async () => {
    await preloadRandoms();
  };

  const stepFillRandomClientSearch = async () => {
      const sel = 'input[name="search"]';
      const input = await waitForSelector(sel);

      const { term } = randoms.clientSearch || {};
      if (!term) throw new Error('Random client search term missing');

      await moveToEl(input);
      // simulate real typing; typeText clears first via setNativeValue("")
      await typeText(sel, term, 40);   // tweak delay (ms/char) if you want faster/slower
      await wait(300);                 // let live search react
  };

  const stepClickRandomTable = async () => {
    if (!randoms.table?.number) throw new Error('No random table selected');

    // Find the number anywhere, but it will be the visible one because findByText filters by visibility
    const re = new RegExp(tr('^{{var1}}\\b', {
      var1: String(randoms.table.number)
    }));
    const numberNode = findByText(re, { root: document, selector: '*' });
    if (!numberNode) throw new Error(tr('Table {{number}} not found in selector', {
      number: randoms.table.number
    }));

    // Move from the <b> number up to the clickable .table box
    const clickable = numberNode.closest('.table') || numberNode;

    // 1) Bring the VISIBLE tables container into the window viewport
    const container = getVisibleTablesContainer();
    if (container) {
      container.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      await wait(500);
    }

    // 2) Now scroll INSIDE that container to the specific table (smooth, nearest)
    clickable.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    await wait(450);

    // 3) Move cursor without re-scrolling and click
    await moveToEl(clickable, { scroll: false });
    await click(clickable);
    await wait(300);

    // 4) Back to top so the form header is visible again
    await smoothScrollTop();
    await centerCursor();
  };

  const stepSetValueSilent = async (s) => {
    setCursorVisible(true);
    setEmphasizeNote(false);
    if (s?.note) setNote(s.note);

    const el = await waitForSelector(s.sel);
    // Don't focus (avoids browser auto-scrolling to the field)
    setNativeValue(el, String(s.value ?? ""));
    el.dispatchEvent(new Event('change', { bubbles: true }));
    await wait(300);
  };

  const scrollWindowToElement = async (el, { topOffset = 90 } = {}) => {
    const y = window.scrollY + el.getBoundingClientRect().top - topOffset;
    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    await wait(550);
  };

  const stepSelectRandomMeat = async (s) => {
    // Show the step note BEFORE moving, so the bubble follows the cursor
    setCursorVisible(true);
    setEmphasizeNote(false);
    if (s?.note) setNote(s.note);

    // Be robust: support both desktop (ReactModal) and mobile (no wrapper)
    const selStr = s?.sel || '.ReactModal__Content select[name="meat"], select[name="meat"]';
    const el = await waitForSelector(selStr);

    await moveToEl(el);
    await wait(200);       // let the note/bubble settle next to the field
    el.focus();
    el.click();            // optional: visually open the dropdown
    await wait(120);

    el.value = String(randoms.meat?.id || '');
    el.dispatchEvent(new Event('input',  { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
    await wait(350);
  };

  const stepScrollTablesAreaIntoView = async (s) => {
    const container = getVisibleTablesContainer();
    if (container) {
      container.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      await wait(500);
    }
    if (s?.note) setNote(s.note);
  };

  const stepSelectRandomCooktype = async (s) => {
    setCursorVisible(true);
    setEmphasizeNote(false);
    if (s?.note) setNote(s.note);

    const selStr = s?.sel || '.ReactModal__Content select[name="cooktype"], select[name="cooktype"]';
    const el = await waitForSelector(selStr);

    await moveToEl(el);
    await wait(200);
    el.focus();
    el.click();
    await wait(120);

    el.value = String(randoms.cooktype?.id || '');
    el.dispatchEvent(new Event('input',  { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
    await wait(350);
  };

  const stepSetSelect = async (s) => {
    // show the note while moving the cursor
    setCursorVisible(true);
    setEmphasizeNote(false);
    if (s?.note) setNote(s.note);

    const selStr = s?.sel;
    const value  = s?.value;
    if (!selStr || value == null) throw new Error('setSelect requires { sel, value }');

    const el = await waitForSelector(selStr);
    await moveToEl(el);
    await wait(150);
    el.focus();
    el.click();
    await wait(100);

    el.value = String(value);
    el.dispatchEvent(new Event('input',  { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
    await wait(250);
  };

  // Define your tour here (selectors must match your DOM)
  const steps = useMemo(() => [
      { type: "note", text: "Welcome! Please do not take any actions while playing this demo to prevent its cancelation." },
      { type: "note", text: "If you want to stop the demo just click somewhere over the screen at any time!" },
      { type: "note", text: "This is a demo account that does not include some features like: Sheduler, Tables, Settings, Reports, Clients, Users & Billing." },
      { type: "note", text: "Get this tour and if you are interested in a full presentation of the service, please contact us!" },
      { type: "note", text: "So.. We'll login to the demo account 'Restaurant' and walk through the basics. Are you ready? Let's go!" },
      { type: "fill", sel: 'input[name="username"]', value: "Manager", note: "Demo username: Manager" },
      { type: "fill", sel: 'input[name="password"]', value: "Manager123!", note: "Demo password: Manager123!" },
      { type: "click", sel: 'button[type="submit"]', note: "Let's Login now" },   
      { type: "waitPath", path: "/reservations-list" }, 
      { type: "note", text: "This is your Reservations screen! We will come back here later again." },
      { type: "note", text: "Let's add a new reservation now.." },
      { type: "click", sel: 'a[href="/reservation-form"]'},
      { type: "waitPath", path: "/reservation-form", note: ""  }, 
      { type: "note", text: "This is your Reservation Form. Here you can add a new reservation. So, let's insert one.." },
      { type: 'preloadRandoms', note: 'Picking some random selections for you..' },
      { type: 'fillRandomClientSearch', note: 'Searching a random client (either first 3 letters of name or 4 digits of phone).' },
      { type: 'setSelect',
        sel: 'select#addedMinutes, select[name="addedMinutes"]',
        value: 10,
        note: "Select date & time or set arrival time relative to now. We choose 10' from now.."
      },
      { type: "fill", sel: 'input[name="people"]', value: "3", note: "Set the number of attendees.." },
      { type: 'scrollTablesArea', note: "Watch the tables as we tweak the attendees — colors adapt to capacity." },
      { type: "setValueSilent", sel: 'input[name="people"]', value: "4", note: "Attendees: 4 - See the tables changing colors" },
      { type: "setValueSilent", sel: 'input[name="people"]', value: "5", note: "Attendees: 5 - Colors update immediately based on capacity" },
      { type: "clickRandomTable", note: "Picking a random table. If it is already reserved just click OK at the prompt that will appear!" },
      { type: "click", sel: 'button[title*="Create reservation"]', note: "Save the reservation" },
      { type: "note", text: "Here you can assign multiple meats for this reservation as preorders if you want." },
      { type: "selectRandomMeat", sel: '.ReactModal__Content select[name="meat"]', note: "Pick a meat" },             
      { type: "selectRandomCooktype", sel: '.ReactModal__Content select[name="cooktype"]', note: "Choose a cook type" }, 
      { type: "waitFor", sel: '.ReactModal__Content button[type="submit"]:not([disabled])' },
      { type: "click",   sel: '.ReactModal__Content button[type="submit"]:not([disabled])', note: "Save assigned meat" },
      { type: "waitPath", path: "/reservations-list" },
      { type: "note", text: "Your reservation was just saved! Here you can see your new reservation (and its assigned meats) and update it if you want." },
      { type: "note", text: "Now we'll peek at the BBQ board to see reservation's assigned meats." },
      { type: "click", sel: 'a[href="/bbq"]' },
      { type: "waitPath", path: "/bbq" },
      { type: "note", text: "Assigned meats appear here with timings, alarms & controls. We'll jump to the Service now.." },
      { type: "click", sel: 'a[href="/service"]' },
      { type: "waitPath", path: "/service" },
      {
        type: "custom",
        note: "Mark the 'Prepare tables' action as done",
        run: async () => {
          const tbl = randoms.table?.number;
          await clickDoneByTableAndType(tbl, 1, { timeout: 12000 }); // 1 = Prepare tables
        }
      },
      { type: "serviceClickSitting", note: "Open Sitting" },
      { type: "waitFor", sel: ".modal-sitting tbody tr:last-child", note: "Pick the reservation we just created" },
      { type: "click",   sel: ".modal-sitting tbody tr:last-child" },
      { type: "waitFor", sel: ".modal-sitting h3 button:not([disabled])", note: "Confirm Sitting" },
      { type: "click",   sel: ".modal-sitting h3 button:not([disabled])" },
      { type: "note", text: "Now that sitting happened, a new ticket must appear to Kitchen. Let's go there.." },
      { type: "click", sel: 'a[href="/kitchen"]', note: "Open Kitchen view to see the ticket" },
      { type: "waitPath", path: "/kitchen" },
      { type: "kitchenClickLastReady", note: "Mark the corresponding kitchen ticket as ready" },
      { type: "click", sel: 'a[href="/service"]', note: "Back to Service view where an action was just generated" },
      { type: "waitPath", path: "/service" },
      {
        type: "custom",
        note: "Let's serve the starter and then mark this action as 'Done'",
        run: async () => {
          const tbl = randoms.table?.number;
          await clickDoneByTableAndType(tbl, 6, { timeout: 12000 }); // 6 = Serve starter
        }
      }, 
      { type: "note", text: "We hope you got the general idea." },
      { type: "note", text: "Explore freely, or replay the tour any time." },
      { type: "note", text: "Contact us for a full presentation of estiasync.com capabilities!" },
      { type: "note", text: "Thank you for watching our tour.." },
  ], []);

  useEffect(() => {
    if (isMobile) {
      setNote('');
      setDeviceBlocked(true);
    } else {
      setDeviceBlocked(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (runningRef.current || isMobile) return;   // don’t start the tour on phone/tablet
    runningRef.current = true;
    
    (async () => {
      try {
        await centerCursor();
		for (let i = 0; i < steps.length; i++) {
			const s = steps[i];
			setProgress(i / steps.length);
			try {
				if (cancelledRef.current) {
					await centerCursor();
					setNote("Demo cancelled due to user interaction.");
					await wait(10);
					return;
				}			
				if (s.type === "note") {
					await centerCursor();
					setCursorVisible(false);
					setEmphasizeNote(true);
					setNoteInstance(n => n + 1); 
					setNote(typeof s.text === "string" ? s.text : String(s.text));
					const dwell = s.ms ?? (typeof s.text === "string" ? s.text.length * 15 : 1500);
					// progress ring fills over the duration of this note
					setProgressAnimMs(dwell * SPEED);
					setNoteTimerMs(dwell * SPEED);       // bubble’s bar matches real wait time
					await wait(dwell);                   // wait uses SPEED internally
					setNoteTimerMs(0);
					continue;
				} else {
					setCursorVisible(true);
					setEmphasizeNote(false);
					if (s.note) setNote(s.note);
					setProgressAnimMs(600);
				}
				switch (s.type) {
				  case 'preloadRandoms':      await stepPreloadRandoms(); break;
				  case 'clickRandomTable':    await stepClickRandomTable(); break;
				  case 'selectRandomMeat':    await stepSelectRandomMeat(s); break;
				  case 'selectRandomCooktype':await stepSelectRandomCooktype(s); break;
				  case 'fillRandomClientSearch': await stepFillRandomClientSearch(); break;
				  case 'setValueSilent': await stepSetValueSilent(s); break;
				  case 'scrollTablesArea': await stepScrollTablesAreaIntoView(s); break;
				}
				if (s.type === 'setSelect') {
				  await stepSetSelect(s);
				}
				if (s.type === "goto") {                      
					navigate(s.path, { replace: true });
					await wait(350);
				}
				if (s.type === "waitPath") {
					await centerCursor(); 
					const start = performance.now();
					while (performance.now() - start < 2000) {
					  if (window.location.pathname.startsWith(s.path)) break;
					  await wait(400);
					}
				}
				if (s.type === "fill") {
					const el = await waitForSelector(s.sel);
					if (s.scrollBefore) {
					   await scrollWindowToElement(el, { topOffset: s.topOffset ?? 90 });
					}
					await moveToEl(el);
					await typeText(s.sel, s.value);
					await wait(400);
				}
				if (s.type === "click") {
					const el = await waitForSelector(s.sel);
					await moveToEl(el);
					await click(el);
					await wait(400);
				}
				if (s.type === "clickText") {
					let el = null;
					const roots = s.within ? s.within.split(',').map(s => document.querySelector(s.trim())).filter(Boolean) : [document];
					for (const root of roots) {
						el = await waitForText(s.text, { root, selector: s.selector || "*" }).catch(() => null);
						if (el) break;
					}
					if (!el) throw new Error(tr('Text not found: {{text}}', {
                      text: s.text
                    }));
					await moveToEl(el);
					await click(el);
					await wait(400);
				}
				if (s.type === "waitFor") {
					const el = await waitForSelector(s.sel);
					await moveToEl(el);
					await wait(150);
				}
				if (s.type === "selectFirst") {
				    const el = await waitForSelector(s.sel);
				    await moveToEl(el);
				    const opts = Array.from(el.options).filter(o => !o.disabled && o.value);
				    if (opts.length) {
					  el.value = opts[0].value;
					  el.dispatchEvent(new Event("change", { bubbles: true }));
				    }
				    await wait(400);
				}
				if (s.type === "kitchenClickLastReady") {
					await clickKitchenLastReady();
					await wait(400);
				}
				if (s.type === "serviceClickSitting") {
					await clickSittingButton();
					await wait(400);
				}
				if (s.type === "custom") {
				  if (typeof s.run === "function") {
					await s.run();
					await wait(400);
				  }
				}

				
			} catch (e) {
				console.log(tr('A demo issue occured: {{message}}', {
                  message: e.message
                }));
				continue;
			} finally {
				setProgress((i + 1) / steps.length);
			}	
        }
      } catch (e) {
        await centerCursor();
        setNote(tr('Demo paused: {{message}}', {
          message: e.message
        }));
      } finally {
        centerCursor();
        await wait(100);
        setNote("");
        onDone?.();
      }
    })();
      
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pause when tab not visible (nicer UX)
  useEffect(() => {
    const onVis = () => { /* could pause timers here if you extend player */ };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  if (deviceBlocked) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 100000,
        background: 'rgba(0,0,0,.6)', display: 'grid', placeItems: 'center'
      }}>
        <div style={{
          background: 'rgba(17,17,17,.95)', color: 'white',
          padding: '18px 20px', borderRadius: 14, maxWidth: 560, textAlign: 'center',
          border: '1px solid rgba(255,165,0,.6)', boxShadow: '0 12px 28px rgba(0,0,0,.35)', margin: 20
        }}>
          <div style={{ fontSize: 16, lineHeight: 1.5 }}>{tr(
            'You have to be connected via desktop, laptop or by using a monitor to feel the total experience of our demo tour.'
          )}</div>
          <button
              onClick={() => {
                try { sessionStorage.removeItem('bbqsync_tour'); } catch {}

                // strip ?tour=1 so it won't auto-rearm anywhere
                try {
                  const url = new URL(window.location.href);
                  if (url.searchParams.has('tour')) {
                    url.searchParams.delete('tour');
                    const qs = url.searchParams.toString();
                    window.history.replaceState({}, '', tr('{{pathname}}{{var2}}{{hash}}', {
                      pathname: url.pathname,

                      var2: qs ? tr('?{{qs}}', {
                        qs
                      }) : '',

                      hash: url.hash
                    }));
                  }
                } catch {}

                // redirect to home (hard replace so the demo can't re-mount)
                window.location.replace('/');
              }}
            style={{
              marginTop: 14, padding: '8px 14px',
              borderRadius: 10, fontWeight: 600, cursor: 'pointer',
              background: 'white', color: '#111', border: '1px solid #ddd'
            }}
          >{tr('OK')}</button>
        </div>
      </div>
    );
  }

  return (
	<>
		<Cursor
		  x={cursor.x}
		  y={cursor.y}
		  clicking={cursor.clicking}
		  visible={cursorVisible}
		  progress={progress}
		  progressAnimMs={progressAnimMs}
		/>
		<FloatingNote x={cursor.x} y={cursor.y} text={note} emphasize={emphasizeNote} timerMs={noteTimerMs} instance={noteInstance} />
    </>
  );
}
